import { DateTime } from 'luxon';
import { Component, Vue } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { pascalCase, snakeCase, paramCase, titleCase } from 'change-case';
import store from '@/store';

@Component
export class Formatter extends Vue {
  @Getter
  private getSetting!: (key: string) => any;

  private options: object = {
    maximumFractionDigits: 2
  };

  // eslint-disable-next-line class-methods-use-this
  isNum(num: number | string): number {
    return !num || isNaN(num as number) ? 0 : parseFloat(num as string);
  }

  number(value: string | number): string {
    const num = this.isNum(value);
    return num.toLocaleString(undefined, this.options);
  }

  currency(value: string | number): string {
    const num = this.isNum(value);
    const lang = this.getSetting('lang') || 'en';
    const currency = this.getSetting('currency') || 'USD';

    if (!lang || !currency) {
      return num.toLocaleString(undefined, this.options);
    }

    return num.toLocaleString(lang, {
      ...this.options,
      style: 'currency',
      currency
    });
  }

  percent(value: string | number): string {
    const num = this.isNum(value);
    return num.toLocaleString(undefined, {
      ...this.options,
      style: 'percent'
    });
  }

  sec(value: string | number): string {
    return `${this.number(value)}sec`;
  }

  bool(v: boolean): string {
    return v ? '✓' : '✕';
  }
}

@Component
export class DateFormatter extends Vue {
  @Getter
  private getSetting!: (key: string) => any;

  date(timestamp: number): string {
    return DateTime.fromMillis(timestamp)
      .setZone(this.getSetting('timezone'))
      .toFormat('dd');
  }

  short(timestamp: number): string {
    return DateTime.fromMillis(timestamp)
      .setZone(this.getSetting('timezone'))
      .toFormat('LLL dd');
  }

  long(timestamp: number): string {
    return DateTime.fromMillis(timestamp)
      .setZone(this.getSetting('timezone'))
      .toLocaleString(DateTime.DATE_HUGE);
  }
}

const formatter = new Formatter({ store });
const dateformatter = new DateFormatter({ store });

export const UtilService = {
  formatter,
  dateformatter,
  toObject(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  },
  toPascal(str: string): string {
    return pascalCase(str);
  },
  toSnake(str: string): string {
    return snakeCase(str);
  },
  toKebab(str: string): string {
    return paramCase(str);
  },
  toTitle(str: string): string {
    return titleCase(str);
  },
  validateEmail(email: string): boolean {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  },
  timestamp() {
    return Math.floor(Date.now() / 1000);
  },
  toEpoch(dateTime: DateTime): number {
    return Math.round(dateTime.toMillis() / 1000);
  },
  isJson(str: string): boolean {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  },
  formatJson(str: string, indent: number = 2): string {
    return JSON.stringify(JSON.parse(str), null, indent);
  },
  trigo(method: 'tan' | 'sin' | 'cos', degree: number) {
    //@ts-ignore
    return Math[method]((Math.PI * degree) / 180);
  }
};
